import { Schema } from "effect";
import type { DefaultRuleId } from "./index";
import { archiveEmailsFromBlockedSendersRule } from "./rules/archive-emails-from-blocked-senders";
import { assignOnConversationCreatedRule } from "./rules/assign-on-conversation-created";
import { assignOnMerchantResponseRule } from "./rules/assign-on-merchant-response";
import { generateConversationInsightsRule } from "./rules/generate-conversation-insights";
import { reopenOnCustomerReplyRule } from "./rules/reopen-on-customer-reply";
import { sendAiResponseOnCustomerReplyRule } from "./rules/send-ai-response-on-customer-reply";
import { unassignOnConversationReopenedRule } from "./rules/unassign-on-conversation-reopened";
import { DefaultRule } from "./types";

/**
 * Accepts either number or string for `sortIndex`. CockroachDB INT8 columns
 * deserialize as strings server-side, while RPC / JSON inputs deserialize as
 * numbers. Both decode to `number`.
 */
const SortIndexSchema = Schema.transform(
  Schema.Union(Schema.Number, Schema.String),
  Schema.Number,
  {
    strict: true,
    decode: (value) => (typeof value === "string" ? Number(value) : value),
    encode: (value) => value,
  },
);

/**
 * Builds the `Schema.Struct` variant for a single rule's decoded config row.
 * Rules without a `configSchema` accept any (unvalidated) JSONB object as
 * `configurations` — the service layer ignores the contents and reads
 * `rule.defaultConfig.configurations` (which is `{}`) instead.
 *
 * Generic over `TId` so the returned variant preserves the rule's id literal
 * in its TypeScript type — that's what makes the union below a true
 * discriminated union at compile time.
 */
function ruleToConfigSchema<TId extends string, TConfig>(
  rule: DefaultRule<TId, TConfig>,
) {
  return Schema.Struct({
    defaultRuleId: Schema.Literal(rule.id),
    enabled: Schema.Boolean,
    sortIndex: SortIndexSchema,
    configurations: (rule.configSchema ?? Schema.Struct({})) as Schema.Schema<
      TConfig,
      unknown
    >,
  });
}

/**
 * Discriminated union of all decoded `default_rule_config` row shapes.
 *
 * Enumerated explicitly (rather than derived from `DEFAULT_RULES` via
 * `Object.values(...).map(...)`) so TypeScript preserves each variant's
 * `defaultRuleId` literal and narrows `configurations` accordingly — e.g.,
 * `if (row.defaultRuleId === "default-send-ai-response-on-customer-reply")`
 * narrows `row.configurations` to that rule's config type.
 *
 * When adding a new default rule, remember to add it to both
 * `DEFAULT_RULES` in the shared index and to this union.
 */
export const DecodedDefaultRuleConfigSchema = Schema.Union(
  ruleToConfigSchema(generateConversationInsightsRule),
  ruleToConfigSchema(archiveEmailsFromBlockedSendersRule),
  ruleToConfigSchema(reopenOnCustomerReplyRule),
  ruleToConfigSchema(assignOnConversationCreatedRule),
  ruleToConfigSchema(assignOnMerchantResponseRule),
  ruleToConfigSchema(sendAiResponseOnCustomerReplyRule),
  ruleToConfigSchema(unassignOnConversationReopenedRule),
);

export type DecodedDefaultRuleConfig =
  typeof DecodedDefaultRuleConfigSchema.Type;

/**
 * Discriminated pair derived from the read-side union. Narrowing on
 * `defaultRuleId` narrows `configurations` to that rule's config type, so
 * passing mismatched fields is a TypeScript error. Shared between the
 * backend repo and frontend write paths.
 */
export type DefaultRuleConfigPair = Pick<
  DecodedDefaultRuleConfig,
  "defaultRuleId" | "configurations"
>;

/**
 * Same idea as `ruleToConfigSchema` but without `sortIndex` — this is
 * the schema used for the batch-update RPC payload, so the HTTP layer
 * decodes each incoming item against its rule's `configSchema` before
 * the handler runs.
 */
function ruleToUpdateSchema<TId extends string, TConfig>(
  rule: DefaultRule<TId, TConfig>,
) {
  return Schema.Struct({
    defaultRuleId: Schema.Literal(rule.id),
    enabled: Schema.Boolean,
    configurations: (rule.configSchema ?? Schema.Struct({})) as Schema.Schema<
      TConfig,
      unknown
    >,
  });
}

/**
 * Discriminated union of per-rule update shapes. Pairs the literal
 * `defaultRuleId` with the matching configurations schema so wire
 * decoding rejects mismatched pairs and the backend handler receives
 * a strongly typed, validated `DefaultRuleConfigUpdate` value with no
 * further decoding needed.
 *
 * Every field is required — batch writers always supply the full
 * `(enabled, configurations)` pair, which keeps the repo SQL uniform.
 * To flip just one field, read the current value from `getDefaultRules`
 * and re-send the other field unchanged.
 *
 * When adding a new default rule, remember to add it here as well as
 * to `DEFAULT_RULES` and `DecodedDefaultRuleConfigSchema`.
 */
export const DefaultRuleConfigUpdateSchema = Schema.Union(
  ruleToUpdateSchema(generateConversationInsightsRule),
  ruleToUpdateSchema(archiveEmailsFromBlockedSendersRule),
  ruleToUpdateSchema(reopenOnCustomerReplyRule),
  ruleToUpdateSchema(assignOnConversationCreatedRule),
  ruleToUpdateSchema(assignOnMerchantResponseRule),
  ruleToUpdateSchema(sendAiResponseOnCustomerReplyRule),
  ruleToUpdateSchema(unassignOnConversationReopenedRule),
);

export type DefaultRuleConfigUpdate = typeof DefaultRuleConfigUpdateSchema.Type;

/**
 * Compile-time exhaustiveness checks: every id in `DEFAULT_RULES` must
 * appear as a variant in both schema unions above. Adding a rule to
 * `DEFAULT_RULES` without also adding it to `DecodedDefaultRuleConfigSchema`
 * and `DefaultRuleConfigUpdateSchema` fails to compile here, so batch
 * writes can't silently skip the new rule.
 */
type _AssertExhaustive<T extends true> = T;
type _DecodedSchemaExhaustive = _AssertExhaustive<
  [DefaultRuleId] extends [DecodedDefaultRuleConfig["defaultRuleId"]]
    ? true
    : false
>;
type _UpdateSchemaExhaustive = _AssertExhaustive<
  [DefaultRuleId] extends [DefaultRuleConfigUpdate["defaultRuleId"]]
    ? true
    : false
>;
// Reference the aliases so unused-type lint doesn't strip them.
export type _DefaultRuleSchemaExhaustivenessProof = [
  _DecodedSchemaExhaustive,
  _UpdateSchemaExhaustive,
];
